/**
 * Custom hook för geocoding med debouncing
 *
 * Features:
 * - Automatisk debouncing (300ms)
 * - Result-pattern för felhantering
 * - Loading states
 * - Clear-funktion
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { GeocodingResult, Result } from '@/types';
import { geocodeAddress } from '@/services/googlemaps';

export interface UseGeocodingResult {
  results: GeocodingResult[];
  isLoading: boolean;
  error: Error | null;
  search: (query: string) => void;
  clear: () => void;
}

const DEBOUNCE_DELAY_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * Hook för att söka efter platser med automatisk debouncing
 */
export function useGeocoding(): UseGeocodingResult {
  const [results, setResults] = useState<GeocodingResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [query, setQuery] = useState('');

  // Referens för att hålla koll på senaste request
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Utför geocoding-sökning
   */
  const performSearch = useCallback(async (searchQuery: string) => {
    // Avbryt pågående request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Tom sökning
    if (!searchQuery || searchQuery.trim().length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Ny abort controller för denna request
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const result: Result<GeocodingResult[]> = await geocodeAddress(searchQuery.trim());

      // Kolla om request avbröts
      if (abortControllerRef.current.signal.aborted) {
        return;
      }

      if (result.success) {
        setResults(result.data);
        setError(null);
      } else {
        setResults([]);
        setError(result.error);
      }
    } catch (err) {
      // Ignorera abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      setResults([]);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Debounced search effect
   */
  useEffect(() => {
    // Rensa tidigare timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Sätt ny timeout
    timeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, DEBOUNCE_DELAY_MS);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, performSearch]);

  /**
   * Uppdatera sökquery (triggar debounced search)
   */
  const search = useCallback((newQuery: string) => {
    setQuery(newQuery);
  }, []);

  /**
   * Rensa sökresultat och query
   */
  const clear = useCallback(() => {
    // Avbryt pågående requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setQuery('');
    setResults([]);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    results,
    isLoading,
    error,
    search,
    clear,
  };
}
